// NOTE: The default diagram skin and the helpers that resolve a semantic role
// to a concrete colour.
//
// Layout never sees a colour: engines emit roles, and only the renderer
// resolves them. That is what lets an organisation's brand (NSAI-5121) reskin
// every diagram without any relayout, and what lets one scene render in both
// light and dark without a second layout pass.
//
// The palette is the shipped default of the `diagram-design` design system:
// white-smoke paper, jet-black ink, atomic-tangerine accent, blue-slate muted.

import {
  EDiagramColorRole,
  type IDiagramFontStacks,
  type IDiagramTheme,
  type TDiagramTokens,
} from "../../types/diagram";

export const DIAGRAM_LIGHT_TOKENS: TDiagramTokens = {
  [EDiagramColorRole.PAPER]: "#f5f5f5",
  [EDiagramColorRole.PAPER_2]: "#ececec",
  [EDiagramColorRole.INK]: "#2d3142",
  [EDiagramColorRole.MUTED]: "#4f5d75",
  [EDiagramColorRole.SOFT]: "#7a8399",
  [EDiagramColorRole.RULE]: "rgba(45,49,66,0.12)",
  [EDiagramColorRole.RULE_SOLID]: "#bfc0c0",
  [EDiagramColorRole.ACCENT]: "#eb6c36",
  [EDiagramColorRole.ACCENT_TINT]: "rgba(235,108,54,0.08)",
  [EDiagramColorRole.LINK]: "#2e5aa8",
};

// NOTE: Inversion rule — same opacities, RGB flipped, and the accent shifted
// brighter so it still reads as focal against dark paper.
export const DIAGRAM_DARK_TOKENS: TDiagramTokens = {
  [EDiagramColorRole.PAPER]: "#2d3142",
  [EDiagramColorRole.PAPER_2]: "#393e53",
  [EDiagramColorRole.INK]: "#f5f5f5",
  [EDiagramColorRole.MUTED]: "#bfc0c0",
  [EDiagramColorRole.SOFT]: "#8e98ac",
  [EDiagramColorRole.RULE]: "rgba(245,245,245,0.12)",
  [EDiagramColorRole.RULE_SOLID]: "rgba(191,192,192,0.25)",
  [EDiagramColorRole.ACCENT]: "#f08a59",
  [EDiagramColorRole.ACCENT_TINT]: "rgba(240,138,89,0.10)",
  [EDiagramColorRole.LINK]: "#6a95d8",
};

// NOTE: No webfont is referenced. The chat HTML preview runs under
// `default-src 'none'` with `font-src data:`, and on-premise installations have
// no outbound network at all, so a diagram that linked Google Fonts would
// silently fall back to a metric-different face and break the measured layout.
// System stacks only until NSAI-5121 embeds the vendored OFL families.
export const DIAGRAM_FONT_STACKS: IDiagramFontStacks = {
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", "Droid Serif", serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
};

export const DIAGRAM_LIGHT_THEME: IDiagramTheme = {
  tokens: DIAGRAM_LIGHT_TOKENS,
  fonts: DIAGRAM_FONT_STACKS,
};

export const DIAGRAM_DARK_THEME: IDiagramTheme = {
  tokens: DIAGRAM_DARK_TOKENS,
  fonts: DIAGRAM_FONT_STACKS,
};

export interface IResolveDiagramThemeProps {
  isDark?: boolean | undefined;
  overrides?: Partial<TDiagramTokens> | undefined;
  fonts?: Partial<IDiagramFontStacks> | undefined;
}

export const resolveDiagramTheme = (
  props: IResolveDiagramThemeProps,
): IDiagramTheme => {
  const { isDark, overrides, fonts } = props;
  const base = isDark === true ? DIAGRAM_DARK_THEME : DIAGRAM_LIGHT_THEME;

  return {
    tokens: { ...base.tokens, ...overrides },
    fonts: { ...base.fonts, ...fonts },
  };
};

export interface IResolveColorProps {
  role: EDiagramColorRole;
  theme: IDiagramTheme;
}

export const resolveDiagramColor = (props: IResolveColorProps): string => {
  const { role, theme } = props;
  return theme.tokens[role];
};
